import {
  ITravel,
  ITravelExtendData,
  ITravelBase,
  ITravelBaseData,
  TRAVEL_NAME_MAX_LENGTH,
  TRAVEL_DESCRIPTION_MAX_LENGTH,
  TRAVEL_SHORT_DESCRIPTION_MAX_LENGTH,
  Accommodation,
  IAccommodation,
  Budget,
  IBudget,
  Taste,
  ITaste,
  Type,
  IType,
  Location,
  ILocation,
  Season,
  ISeason,
  Step,
  IStep,
  Mark,
  ITravelFilter,
  ITravelCriteriaFull,
  ITag,
  ITravelInfoIdsExtended,
  Tag, IAccommodationData, IStepData, ISeasonData, ILocationData, ITasteData, ITypeData, ITravelInfoIdsExtendedData
} from './index'
import {
  Category,
  CategoryArray,
  Customer,
  ICustomer,
  IImage,
  Image
} from '../index'
import { TravelFormat, TravelFormatArray } from '../abstract/travelFormat.model'
import { ITravelAdditions } from './additions/travelAdditions'


function validateDescription(self: any): boolean {
  let desc = self.description
  return !!desc && desc.length <= TRAVEL_DESCRIPTION_MAX_LENGTH
}

export class TravelBase implements ITravelBase {
  readonly id?: number
  name?: string
  price: number

  constructor(data?: ITravelBaseData) {
    this.id = data?.id
    this.baseUpdate(data);
    let price = data?.price;
    price = (typeof price === 'string') ? parseFloat(price) : price
    this.price = price || 0
  }

  validate(): boolean {
    return this.validateName() && this.validatePrice()
  }

  validatePrice(): boolean {
    return this.price > 0
  }

  validateName(): boolean {
    let name = this.name
    return !!name && name.length <= TRAVEL_NAME_MAX_LENGTH
  }

  protected baseUpdate(data?: ITravelBaseData): void {
    this.name = data?.name || this.name
  }

  update(data?: ITravelBaseData): void {
    this.baseUpdate(data);
  }

}


export class TravelInfoIdsExtended implements ITravelInfoIdsExtended {
  description?: string

  tourist_min?: number
  tourist_max?: number
  duration_days_min?: number
  duration_days_max?: number
  budget_min?: number
  budget_max?: number

  accommodation?: number[]
  travel_type?: number[]
  taste?: number[]
  locations?: number[]
  season?: number[]

  constructor(data?: ITravelInfoIdsExtendedData) {
    this.update(data)
  }

  validate(): boolean {
    return this.validateDescription();
  }

  validateDescription(): boolean {
    return validateDescription(this);
  }

  update(data?: ITravelInfoIdsExtendedData) {
    this.description = data?.description || ''
    this.tourist_min = data?.tourist_min
    this.tourist_max = data?.tourist_max
    this.duration_days_min = data?.duration_days_min
    this.duration_days_max = data?.duration_days_max
    this.budget_min = data?.budget_min
    this.budget_max = data?.budget_max
    this.accommodation = data?.accommodation
    this.travel_type = data?.travel_type
    this.taste = data?.taste
    this.locations = data?.locations
    this.season = data?.season
  }

}


export class Travel extends TravelBase implements ITravel {
  description?: string;

  tourist_min: number;
  tourist_max?: number;
  duration_days?: number;
  category?: Category;
  travel_format?: TravelFormat;
  mark?: Mark;

  general_image: IImage;
  images?: IImage[];

  accommodation?: IAccommodation[];
  travel_type?: IType[];
  taste?: ITaste[];
  budgets?: IBudget[];
  locations?: ILocation[];
  season?: ISeason[];

  steps?: IStep[];

  customer?: ICustomer;

  travel_additions?: ITravelAdditions;

  constructor(data?: ITravelExtendData) {
    
    super(data);
    this.baseUpdate(data);

    this.tourist_min = data?.tourist_min || 1;
    let max = data?.tourist_max;
    this.tourist_max = max ? (max < this.tourist_min ? this.tourist_min : max) : undefined;
    this.duration_days = data?.duration_days;

    // files
    this.general_image = new Image(data?.general_image);
    this.images = data?.images?.map(image => new Image(image));

    // owner
    let customer = data?.customer;
    this.customer = customer ? new Customer(customer) : undefined;

    // criteria without category???
    this.accommodation = data?.accommodation?.map(acc => new Accommodation(acc));
    this.travel_type = data?.travel_type?.map(type => new Type(type));
    this.taste = data?.taste?.map(t => new Taste(t));
    this.locations = data?.locations?.map(loc => new Location(loc));
    this.season = data?.season?.map(s => new Season(s));

    this.budgets = data?.budgets?.map(budget => new Budget(budget));
    this.steps = data?.steps?.map(step => new Step(step));
    this.travel_additions = data?.travel_additions;
    this.travel_format = data?.travel_format;
  }

  protected baseUpdate(data?: ITravelExtendData): void {
    super.baseUpdate(data)
    this.description = data?.description || this.description
    this.category = data?.category || this.category
    this.travel_format = data?.travel_format || this.travel_format
    this.mark = data?.mark || this.mark
  }

  private updateItems<T, TData>(field: string, items?: TData[], C?: any): void {
    items?.forEach((item: TData) => {
      let records = (this as any)[field];
      let record = records?.find((i: T) => (i as any).id === (item as any).id);
      if (record) {
        record.update(item);
      } else if (C) {
        records = records || [];
        records.push(new C(item));
      }
    });
  }

  update(data?: ITravelExtendData): void {
    this.baseUpdate(data);
    this.updateItems<IAccommodation, IAccommodationData>('accommodation', data?.accommodation, Accommodation);
    this.updateItems<IType, ITypeData>('travel_type', data?.travel_type, Type);
    this.updateItems<ITaste, ITasteData>('taste', data?.taste, Taste);
    this.updateItems<ILocation, ILocationData>('locations', data?.locations, Location);
    this.updateItems<ISeason, ISeasonData>('season', data?.season, Season);
    this.updateItems<IStep, IStepData>('steps', data?.steps, Step);
  }

  shortDescription(): string {
    let desc = this.description
    return desc ? (
      desc.length > TRAVEL_SHORT_DESCRIPTION_MAX_LENGTH ?
        (desc.slice(0, TRAVEL_SHORT_DESCRIPTION_MAX_LENGTH - 3) + '...') :
        desc
    ) : '';
  }

  validate(): boolean {
    return (
      super.validate() && this.validateDescription() && this.validateShortDescription() &&
      this.validateTouristsCount() && this.validateDuration()
    )
  }

  validateDescription(): boolean {
    return validateDescription(this);
  }

  validateShortDescription(): boolean {
    let short = this.shortDescription()
    return !!short && short.length <= TRAVEL_SHORT_DESCRIPTION_MAX_LENGTH
  }

  validateTouristsCount(): boolean {
    let max = this.tourist_max
    let min = this.tourist_min
    return min > 0 && (!max || max > min)
  }

  validateDuration(): boolean {
    let duration = this.duration_days
    return !duration || duration > 0
  }

  private get travelCriteria() {
    return {
      accommodation: this.accommodation,
      budget: [this.minBudget, this.maxBudget],
      travel_type: this.travel_type,
      taste: this.taste,
      locations: this.locations,
      tourist: [this.tourist_min, this.tourist_max],
      duration_days: this.duration_days,
      season: this.season,
    }
  }

  get minBudget(): number | undefined {
    return this.budgets?.reduce((min: number | undefined, budget: IBudget) => {
      let budget_min = budget.budget_from;
      return budget_min ? (min ? (budget_min < min ? budget_min : min) : budget_min) : min;
    }, undefined);
  }

  get maxBudget(): number | undefined {
    return this.budgets?.reduce((max: number | undefined, budget: IBudget) => {
      let budget_max = budget.budget_to;
      return budget_max ? (max ? (budget_max > max ? budget_max : max) : budget_max) : max;
    }, undefined);
  }

  get tags(): ITag[] {
    let fields = Object.keys(this.travelCriteria);
    let category = this.category as string;
    const travel_format = this.travel_format as string;
    let res = fields.map((field: string) => new Tag({
      name: field,
      value: (this.travelCriteria as any)[field]
    }));
    if (category) {
      res.push(new Tag({
        name: 'category',
        value: category
      }))
    }
    if (travel_format) {
      res.push(new Tag({
        name: 'travel_format',
        value: travel_format,
      }));
    }

    return res;
  }
}

export class TravelFilter implements ITravelFilter {
  tourist_min?: number
  tourist_max?: number
  duration_days?: number

  season?: string[]
  accommodation?: string[]
  travel_type?: string[]
  taste?: string[]
  locations?: string[]

  budget_from?: number
  budget_to?: number
  search?: string
  category?: Category[]

  constructor() {
    this.reset();
  }

  reset(): void {
    this.tourist_min = 1;
    this.tourist_max = undefined;
    this.duration_days = undefined;
    this.accommodation = [];
    this.travel_type = [];
    this.taste = [];
    this.locations = [];
    this.category = [];
    this.season = [];
    this.budget_from = undefined;
    this.budget_to = undefined;
    this.search = undefined;
  }
}

export class TravelCriteriaFull implements ITravelCriteriaFull {
  
  travel_format?: TravelFormat[];
  accommodation?: IAccommodation[];
  travel_type?: IType[];
  taste?: ITaste[];
  locations?: ILocation[];
  season?: ISeason[];
  category?: Category[];
  travel_additions?: ITravelAdditions;

  constructor() {
    this.reset();
  }

  reset() {
    this.travel_format = TravelFormatArray.map((travelForm: TravelFormat) => travelForm);
    this.accommodation = [];
    this.travel_type = [];
    this.taste = [];
    this.locations = [];
    this.season = [];
    this.category = CategoryArray.map((cat: Category) => cat);
    this.travel_additions = undefined;
  }
}
